/* eslint pinterest/no-jquery-outside-of-render:0 */
// vendor libs
import AppVersion from 'app/common/lib/AppVersion';
import BackboneEventsShim from 'app/common/lib/BackboneEventsShim';
import canUseHistory from 'app/common/lib/util/canUseHistory';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import context from 'app/common/lib/context';
import experiments from 'app/common/lib/experiments';
import forEach from 'lodash/forEach';
import ga from 'app/common/lib/ga';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import Logging from 'app/common/lib/Logging';
import NavigateData from 'app/common/lib/NavigateData';
import routes from 'app/common/lib/routes';
import SafeLink from 'app/common/lib/SafeLink';
import store from 'app/common/redux/store/singleton';
import trackingTimer from 'app/common/lib/TrackingTimer';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';
import { instance as pubSub } from 'app/common/lib/PubSub';
import { logAutoClientRefreshDesktopEvent } from 'app/packages/auto-client-refresh';
import { routeChange } from 'app/common/redux/actions/history';

const titleLoader = () => import(/* webpackChunkName: "TitleCommon" */ 'app/common/lib/Title');

const routerLog = Logging.getLogger('router');

const router = {};

/** @enum {number} */
router.NavStatus = {
  READY: 1,
  TRANSITIONING: 2,
  CANCELED: 3,
};

/** @const */
const positionalRegex = /\$\{(\d+)\}/g;
/** @const */
const querystringRegex = /\?\{(\w+)\}/g;
/** @const */
const namedRegex = /\$\{(\w+)\}/g;
/** @const */
const splitPathAndQueryRegex = /^([^\?]*?)\?(.*)$/;

const sub = function(str, parts, params, namedGroups) {
  /* Replace "${0}" in a string with the item at the
   * corresponding index in parts. Replace "?{x}"
   * with the key "x" found in params. If there were any
   * namedGroups (":x"), replace them as well with the
   * values from parts.
   */
  let clearMissingParam = false;
  str = str.replace(querystringRegex, (m, name) => {
    const value = params[name];
    if (value === undefined) {
      clearMissingParam = true;
    }
    return value;
  });
  // Make sure any missing query parameters are set to null.
  if (clearMissingParam) {
    return null;
  }

  str = str.replace(positionalRegex, (m, index) => parts[parseInt(index, 10)]);

  if (namedGroups) {
    str = str.replace(namedRegex, (m, name) => {
      const index = namedGroups[name];
      return parts[index];
    });
  }

  return str;
};

const subRoutePart = function(obj, parts, params, namedGroups) {
  /* Update on objects keys and values with ones that
   * have been subbed using the above `sub` function.
   */
  let newValue = obj;
  if (isObject(obj)) {
    newValue = {};
    forEach(obj, (value, key) => {
      newValue[subRoutePart(key, parts, params, namedGroups)] = subRoutePart(
        value,
        parts,
        params,
        namedGroups,
      );
    });
  } else if (isString(obj)) {
    newValue = sub(obj, parts, params, namedGroups);
  }
  return newValue;
};

/**
 * Information about a route.  This corresponds to the Denzel "Route" class.
 * The fields in this object contain substitution markers, and substitutions
 * are performed as part of matching a requested path to a route.
 * @constructor
 * @param {string} pathPattern a regex pattern string
 */
const Route = function(pathPattern) {
  /**
   * @type {!RegExp}
   * @protected
   */
  this.pathPattern = new RegExp(pathPattern, 'i');

  /**
   * @type {string}
   * @protected
   */
  this.rawPathPattern = null;

  /**
   * @type {string}
   * @protected
   */
  this.module = '';

  /**
   * @type {!Object.<string,number>}
   * @protected
   */
  this.named = {};

  /**
   * @type {!Object}
   * @protected
   */
  this.options = {};

  /**
   * @type {Object}
   * @protected
   */
  this.resource = null;

  /**
   * @type {!Object}
   * @protected
   */
  this.extraContent = {};
};

/**
 * @param {!Object.<string,?>} plain
 * @return {!Route}
 */
Route.fromPlainObject = function(plain) {
  const pathPattern = plain.pathPattern;
  if (!pathPattern) {
    throw new Error('pathPattern is required');
  }

  const route = new Route(pathPattern);

  if ('rawPathPattern' in plain) {
    route.rawPathPattern = plain.rawPathPattern;
  }
  if ('module' in plain) {
    route.module = plain.module;
  }
  if ('named' in plain) {
    route.named = plain.named;
  }
  if ('options' in plain) {
    route.options = plain.options;
  }
  if ('resource' in plain) {
    route.resource = plain.resource;
  }
  if ('extraContent' in plain) {
    route.extraContent = plain.extraContent;
  }

  return route;
};

/**
 * Information about a matched route.  This corresponds to the Python "RouteMatch" class.
 * Marker substitution has been performed, unlike the Route object which has
 * substitution markers.
 * @constructor
 */
const RouteMatch = function() {
  /** type {RegExp} */
  this.pathPattern = null;

  /** @type {string} */
  this.module = '';

  /** @type {!Object.<string,number>} */
  this.named = {};

  /** @type {!Object} */
  this.options = {};

  /** @type {?ResourcePlainObject} */
  this.resource = null;

  /** @type {!Object} */
  this.extraContent = {};

  /** @type {NavigateData} */
  this.navigateData = null;
};

/**
 * Resolves URLs to routes and interacts with browser history API.
 * @constructor
 */
const Router = function() {
  /** @type {!Array.<!Route>} */
  this.routes = [];

  /** @type {string} */
  this.lastUrl = __SERVER__ ? null : window.location.href;

  this.log = Logging.getLogger('Router');

  this.navigateStatus = router.NavStatus.READY;
};

Object.assign(Router.prototype, /** @lends {Router.prototype} */ BackboneEventsShim);

Object.assign(
  Router.prototype,
  /** @lends {Router.prototype} */ {
    start() {
      forEach(routes.list(), (plainRoute) => {
        const route = Route.fromPlainObject(plainRoute);
        this.addRoute(route);
      });

      if (!canUseHistory()) {
        return;
      }

      let windowLocationHref = window.location.href;
      urlUtil.setEntryUrl(windowLocationHref);

      // Check that the URL in window.location matches the canonicalUrl.
      // - If it doesn't replace it with the canonical URL.
      if (window.history.replaceState) {
        const canonicalElem = document.querySelector('link[rel=canonical]');
        let canonicalUrl = null;

        if (canonicalElem) {
          canonicalUrl = /** @type {string} */ (canonicalElem.getAttribute('href'));
        }

        // Only allow replace state for auth to prevent url change in board klp canonicalization experiment
        // Unauth redirect is done 301
        if (windowLocationHref !== canonicalUrl && user.isAuthenticated()) {
          const state = 'state' in window.history ? window.history.state : {};
          try {
            const url = urlUtil.appendQueryString(canonicalUrl, urlUtil.getQueryStringParams());
            if (user.isAuthenticated() || canonicalUrl.indexOf('/pin/') === -1) {
              window.history.replaceState(state, document.title, url);
            }
          } catch (e) {
            this.log.error('replaceState failed', e);
          }
          this.lastUrl = windowLocationHref;
        }
      }

      // Chrome <v34 and Safari have quirky popstate behavior:
      // - A 'popstate' is fired on every page load immediately after 'load'
      //   fires, even if the user didn't interact with browser history.
      // - 'popstate' never fires before 'load'.  Instead, the single 'popstate'
      //   that fires after 'load' contains the state and URL of latest popstate
      //   event that should have fired.
      // This behavior is out-of-spec.
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onpopstate
      window.addEventListener(
        'popstate',
        (evt) => {
          windowLocationHref = window.location.href;

          this.log.debug('popstate fired, lastUrl', this.lastUrl, ' new URL ', windowLocationHref);
          if (evt.state && evt.state.overridePopstate) {
            return;
          }
          if (this.lastUrl === windowLocationHref) {
            this.log.debug('ignoring spurious popstate event');
          } else {
            // In unauth, we strip query params. If we are unauth, don't fire onStateChange as a result of our own
            // URL stripping.
            let urlTest = this.lastUrl.split('?')[0];

            // If we don't have a trailing slash, add it so we have a clean comparison since canonical urls always
            // have a trailing slash
            if (urlTest.substr(-1) !== '/') {
              urlTest += '/';
            }

            if (!user.isAuthenticated() && urlTest === windowLocationHref) {
              this.log.debug(
                'stripped query params and pathname of location equal to before, ' +
                  'ignoring popstate event',
              );
            } else {
              this.onStateChange(evt);
            }
          }
          this.lastUrl = windowLocationHref;
          store.instance.dispatch(
            routeChange('POP', { pathname: window.location.pathname + window.location.search }),
          );
        },
        false,
      );
    },

    replaceState(pathAndQuery) {
      const state = 'state' in window.history ? window.history.state : {};
      window.history.replaceState(state, document.title, pathAndQuery);
      store.instance.dispatch(routeChange('REPLACE', { pathname: pathAndQuery }));
      titleLoader().then(({ default: Title }) => {
        Title.getInstance().setDefaultTitleText();
      });
      this.lastUrl = window.location.href;
    },

    /**
     * Uses pushState to set the url and does other related bookkeeping.
     *
     * @param {string} pathAndQuery The path and optional query string that is URI-encoded.
     */
    pushState(pathAndQuery) {
      // Remove the invalidated canonical link
      const canonicalLink = document.querySelector('link[rel=canonical]');
      if (canonicalLink) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
      window.history.pushState({}, document.title, pathAndQuery);
      store.instance.dispatch(routeChange('PUSH', { pathname: pathAndQuery }));
      titleLoader().then(({ default: Title }) => {
        Title.getInstance().setDefaultTitleText();
      });
      this.lastUrl = window.location.href;
    },

    setStatus(status) {
      this.navigateStatus = status;
    },

    getStatus() {
      return this.navigateStatus;
    },

    /**
     * Navigates to a location.
     *
     * @param {string} url The path and optional query string that is URI-encoded.
     * @param {NavigateData=} _navigateData
     * @param {{fromPopState:boolean}=} options Should we use pushState?
     */
    navigate(url, _navigateData, options) {
      // If current page is unauth react, full page reload will be necessary for denzel navigation
      let previousRouteMatch = null;
      if (!user.isAuthenticated()) {
        const previousPathAndQuery = urlUtil.normalizeUrl(this.lastUrl);
        previousRouteMatch = this.resolve(previousPathAndQuery);
      }

      // Redirect all offsite and open-in-new-tab urls to our offsite handler.
      _navigateData = _navigateData || new NavigateData();

      if (
        urlUtil.isOffsiteUrl(url) ||
        (_navigateData.evt && _navigateData.evt.data && _navigateData.evt.data.useNewTab)
      ) {
        let pinId = null;
        let csrId = null;

        if (_navigateData.evt && _navigateData.evt.data) {
          if (_navigateData.evt.data.csrId) {
            csrId = _navigateData.evt.data.csrId;
          } else if (_navigateData.evt.data.pinId) {
            pinId = _navigateData.evt.data.pinId;
          }
        }

        SafeLink.open(url, pinId, csrId);

        return;
      }

      const pathAndQuery = urlUtil.normalizeUrl(url);
      const fromPopState = options && !!options.fromPopState;

      if (!fromPopState) {
        // If the server has a newer version of JS and CSS, then force
        // a full-page load.
        if (AppVersion.isNewVersionRequired()) {
          logAutoClientRefreshDesktopEvent(
            'refresh',
            'denzel_new_version_required_navigate',
            context,
          );
          changeWindowLocation(url, false);
          return;
        }

        /** @type {urlUtil.UrlClass} */
        const urlClass = urlUtil.getUrlClass(pathAndQuery);

        if (urlClass === urlUtil.UrlClass.INVALID) {
          routerLog.error('unexpected UrlClass', urlClass, pathAndQuery);
          return;
        }

        // Do a full page refresh for browsers without pushState, or if forceRefresh is set
        const shouldForceRefresh =
          _navigateData.evt && _navigateData.evt.data && _navigateData.evt.data.forceRefresh;
        if (
          !canUseHistory() ||
          urlClass === urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN ||
          shouldForceRefresh === PCONST.ForceRefreshType.PAGE
        ) {
          logAutoClientRefreshDesktopEvent('refresh', 'denzel_should_force_refresh', context);
          changeWindowLocation(pathAndQuery, false);
          return;
        }

        if (_navigateData.evt && _navigateData.evt.data && _navigateData.evt.data.newTab) {
          logAutoClientRefreshDesktopEvent('refresh', 'denzel_new_tab', context);
          changeWindowLocation(url, true);
          return;
        }

        // urlClass is SAME_ORIGIN
        _navigateData.fromNavigate = true;

        this.pushState(pathAndQuery);
      } else {
        pubSub.publish(PCONST.PUBSUB_CHANNEL_SITE, PCONST.PUBSUB_TOPIC_NAVIGATE_STARTED, { url });
      }

      /**
       * This is how a user navigates out of Denzel's router and into Pure React (aka react-router).
       *
       * The idea is that the Denzel frontend doesn't know about pure react, but ngapp (Django/Python) does.
       *
       * If a user is in the pure react experiment, but for some reason is seeing a Denzel page:
       *   Since the frontend router isn't able to resolve pure react routes, trigger a full page load
       *   on all navigations. ngapp gives precedence to pure_react_router.py when resolving (auth) routes,
       *   so routes that are supported by pure react will render the pure react app.
       *
       * Talk to @bca if you are making related changes.
       */
      const pureReactSterlingExpGroups = ['employees', 'enabled', 'full_page_node'];
      if (
        pureReactSterlingExpGroups.includes(
          experiments.getInstance().v2GetGroup('sterling_pure_react'),
        )
      ) {
        // Limited login users still see the Denzel experience.
        if (!user.isLimitedLogin()) {
          logAutoClientRefreshDesktopEvent('refresh', 'denzel_limited_login', context);
          changeWindowLocation(pathAndQuery, false);
          return;
        }
      }

      this.navigateData = _navigateData;

      const routeMatch = this.resolve(pathAndQuery);

      this.log.debug('route matched', routeMatch, pathAndQuery);
      if (routeMatch) {
        // potential issue - other modules listening on this need to go first
        this.analyzeRoute(routeMatch).then(
          () => {
            if (this.routeMatchOptions) {
              routeMatch.options = this.routeMatchOptions;
              delete this.routeMatchOptions;
            }
            if (
              !user.isAuthenticated() &&
              (routeMatch.extraContent.react_only ||
                (!routeMatch.extraContent.react_only &&
                  previousRouteMatch &&
                  previousRouteMatch.extraContent.react_only))
            ) {
              // if unauth and moving from between denzel pages and react pages, refresh
              logAutoClientRefreshDesktopEvent(
                'refresh',
                'denzel_bounce_between_react_and_denzel',
                context,
              );
              changeWindowLocation(pathAndQuery, false);
            } else if (
              (context.instance.site_type === PCONST.thrift.SiteType.STERLING ||
                context.instance.site_type === PCONST.thrift.SiteType.ANALYTICS) &&
              user.isAuthenticated() &&
              routeMatch.extraContent.react_only
            ) {
              // if going to a react only sterling or analytics page, refresh
              logAutoClientRefreshDesktopEvent('refresh', 'denzel_react_sterling', context);
              changeWindowLocation(pathAndQuery, false);
            } else {
              this.trigger(PCONST.EVENT_ROUTE_MATCHED, routeMatch);
            }
          },
          (redirectUrl) => {
            // Navigate was canceled because of a route requirement - redirect to a different url
            this.navigate(redirectUrl, new NavigateData());
          },
        );
      } else {
        this.trigger(PCONST.EVENT_ROUTE_NO_MATCH, pathAndQuery);
      }
    },

    /**
     * Try to resolve a route with a given path and query string.
     *
     * @param {RouteMatch} routeMatch A matched route
     * @return {!Promise.<!string>} A rejected promise with the redirect url if applicable, otherwise resolved
     */
    analyzeRoute(routeMatch) {
      this.log.debug('onRoute', routeMatch);
      ga.trackPageview(routeMatch.module);

      const moduleName = routeMatch.module;

      if (routeMatch.extraContent.perf_log) {
        trackingTimer.start(moduleName, 'NavigateToRender');
      }

      return new Promise((resolve, reject) => {
        // Hard navigate because our app version is invalid
        if (AppVersion.isNewVersionRequired()) {
          logAutoClientRefreshDesktopEvent(
            'refresh',
            'denzel_new_version_required_analyzeRoute',
            context,
          );
          window.location.reload();
          return;
        }

        if (routeMatch.extraContent.require_explicit_login && user.get('autologin')) {
          reject(urlUtil.getLoginUrl());
        }
        if (
          !user.isAuthenticated() &&
          context.instance.is_sterling_on_steroids &&
          context.instance.site_type === PCONST.thrift.SiteType.STERLING
        ) {
          // oh snap, the api access token has expired.
          changeWindowLocation('/admin/oauth/reset_access_token/');
        }

        if (routeMatch.extraContent.require_authentication && !user.isAuthenticated()) {
          // Pass the last URL in as the 'prev' value so the user doesn't end up in an infinite loop
          // when trying to exit the signup flow
          // TODO(chris): fromHistoryUid isn't guaranteed to be a URL. Find a better way.
          if (context.instance.site_type === PCONST.thrift.SiteType.STERLING) {
            // '/join' route doesn't exist on sterling
            reject('/');
          } else {
            reject(urlUtil.getRegisterUrl({ prev: routeMatch.navigateData.fromHistoryUid }));
          }
        }

        // TODO(tracy) Take this out after Sterling is rolled out and we're not whitelisting access.
        const hasAdvertiserAccount = user.get('is_partner') && context.instance.advertiser;
        const hasSterlingAccess = context.instance.has_sterling_access && hasAdvertiserAccount;
        const hasAdsApiAccess = context.instance.has_ads_api_access && hasAdvertiserAccount;

        if (
          routeMatch.extraContent.require_ads_api_or_sterling_access &&
          !(hasSterlingAccess || hasAdsApiAccess)
        ) {
          changeWindowLocation('/', false);
        }

        if (routeMatch.extraContent.require_sterling_access && !hasSterlingAccess) {
          // TODO(tatiana): Overload reject to fulfill this
          changeWindowLocation('/', false);
        }

        // TODO(tracy) Take this out after we have analytics for all users (not just partners).
        if (routeMatch.extraContent.require_business_user && !user.get('is_partner')) {
          reject('/');
        }

        if (
          routeMatch.extraContent.require_super_user &&
          !context.instance.is_sterling_on_steroids
        ) {
          reject('/');
        }

        const sterlingRedirectUrl = this.getSterlingRedirectUrl(routeMatch);
        if (sterlingRedirectUrl) {
          // pass options from old route to new route.  If we ever stop using query string params
          // to indicate which step of the promote pin form we're on, this can probably die.
          this.routeMatchOptions = routeMatch.options;
          reject(sterlingRedirectUrl);
        }

        resolve();
      });
    },

    /** Redirect from "/" to "/advertiser/id" for auth Sterling homepage requests.
     * we don't need to explicitly check for auth here because unauth requests won't have
     * context.advertiser.
     */
    getSterlingRedirectUrl(routeMatch) {
      const advertiser = context.instance.advertiser;
      let redirectUrl = null;
      if (routeMatch.extraContent.sterling_redirect_url && advertiser) {
        const splitRedirectUrl = routeMatch.extraContent.sterling_redirect_url.split('%s');
        const advertiserId = advertiser.id;
        if (advertiserId) {
          redirectUrl = splitRedirectUrl[0] + advertiserId + splitRedirectUrl[1];
        }
      }
      return redirectUrl;
    },

    /**
     * Try to resolve a route with a given path and query string.
     *
     * @param {string} pathAndQuery A path and optional query string that is URI-encoded.
     * @return {RouteMatch} The matched route or null if not found.
     */
    resolve(pathAndQuery) {
      const routeMatch = this.resolvePathToRoutePattern(pathAndQuery);
      if (
        routeMatch.length === 1 ||
        (routeMatch.length > 1 && !routeMatch[0].extraContent.exp_name)
      ) {
        return routeMatch[0];
      } else if (routeMatch.length > 1) {
        const isAuthenticated = user.isAuthenticated();
        // matched_routes is a list of routes that match the same url
        // Assume that the exp_name of each route is the same
        const exp_name = routeMatch[0].extraContent.exp_name;
        let exp_group = experiments.getInstance().v2GetGroup(exp_name) || '';

        const cookie_exp_overwrite = routeMatch[0].extraContent.exp_group_overwrite_unauth;
        if (!user.isAuthenticated() && cookie_exp_overwrite) {
          const cookies = context.instance.cookies;
          if (cookies && cookies[exp_name]) {
            exp_group = cookies[exp_name];
          }
        }

        let fallback_route = null;
        let control_route = null;
        for (let i = 0; i < routeMatch.length; i += 1) {
          const extra = routeMatch[i].extraContent;
          if (extra.exp_group === exp_group) {
            return routeMatch[i];
          }
          if (extra.exp_fallback_auth && isAuthenticated) {
            fallback_route = routeMatch[i];
          }
          if (extra.exp_fallback_unauth && !isAuthenticated) {
            fallback_route = routeMatch[i];
          }
          if (extra.exp_group === 'control') {
            control_route = routeMatch[i];
          }
        }

        if (!fallback_route) {
          fallback_route = control_route;
        }

        return fallback_route;
      } else {
        return undefined;
      }
    },

    resolvePathToRoutePattern(pathAndQuery) {
      let path = null;
      let queryString = null;
      const parts = splitPathAndQueryRegex.exec(pathAndQuery);

      if (parts) {
        path = parts[1];
        queryString = parts[2];
      } else {
        path = pathAndQuery;
      }

      // Match routes below based on the decoded path.
      //
      // NOTE(dcp): This does not handle poorly encoded URIs.  Today, this is not a problem,
      // but it might be in the future.  Poorly encoded URIs come from 3rd party sites (e.g.,
      // use of the "Save" button).  An example of how to recover is given in Url.js ::
      // urlUtil.getQueryStringParams.
      let decodedPath = decodeURIComponent(path);

      // Add a trailing slash if not present to match expected route format
      if (decodedPath.slice(-1) !== '/') {
        decodedPath += '/';
      }

      // Added support for multiple routes with the same url,
      // switched using exp_name and exp_group. Iterate over our
      // routes until we hit all possible matches, assuming that
      // repeated urls are adjacent in the list of routes

      const routeMatch = [];
      let foundMatch = false;

      for (let i = 0; i < this.routes.length; i += 1) {
        const route = this.routes[i];
        const result = route.pathPattern.exec(decodedPath);
        if (result) {
          const args = result.slice(1);
          routeMatch.push(this.subRoute(route, args, queryString));
          foundMatch = true;
        } else if (foundMatch) {
          break;
        }
      }

      // Clear navigateData for the next route.
      this.navigateData = new NavigateData();

      return routeMatch;
    },

    /**
     * Add a new route.
     *
     * @param {!Route} route The route to add.
     */
    addRoute(route) {
      this.routes.push(route);
    },

    /**
     * @param {!Route} route
     * @param {!Array.<string>} matches Arguments from the route path
     * @param {string} queryString
     * @return {!RouteMatch}
     */
    subRoute(route, matches, queryString) {
      const params = urlUtil.getQueryStringParams(queryString);

      const match = new RouteMatch();
      match.pathPattern = route.pathPattern;
      match.rawPathPattern = route.rawPathPattern;

      // Perform substitution on each piece of the route.
      match.module = subRoutePart(route.module, matches, params, route.named);
      match.named = subRoutePart(route.named, matches, params, route.named);
      match.options = subRoutePart(route.options, matches, params, route.named);
      match.resource = subRoutePart(route.resource, matches, params, route.named);
      match.extraContent = subRoutePart(route.extraContent, matches, params, route.named);

      match.navigateData = this.navigateData;

      return match;
    },

    onStateChange(e) {
      this.navigate(window.location.pathname + window.location.search, new NavigateData(), {
        fromPopState: true,
      });
    },
  },
);

export const instance = new Router();
export const NavStatus = router.NavStatus;

export { router as default, Router as RouterClass, Route as RouteClass };

router.instance = instance;
